import type { ReactNode } from 'react';

// 11c §4.5 — the overview body.
//
// Left column: the finding, then the work, then the record's history.
// Right rail: the facts about the record itself, which are reference material
// rather than a narrative and so never compete with the left column for
// attention.
//
// Every value below comes off the customer record or the calls the profile
// already makes. Nothing here needs an endpoint that does not exist.

export interface OverviewTicket {
  id: string;
  number: string;
  subject: string;
  status: string;
  priority: string;
  createdAt: string;
  resolutionDueAt?: string;
  firstResponseDueAt?: string;
  firstResponseAt?: string;
}

export interface OverviewActivity {
  id: string;
  type: string;
  description: string;
  channel?: string;
  performedBy?: string;
  performedByType?: 'customer' | 'agent' | 'system';
  createdAt: string;
}

export interface OverviewNote {
  id: string;
  content: string;
  createdBy?: string;
  createdAt: string;
}

export interface ProfileOverviewProps {
  attention: { headline: string; body: ReactNode } | null;
  tickets: OverviewTicket[];
  totalTickets: number;
  activities: OverviewActivity[];
  notes: OverviewNote[];
  record: { label: string; value: string }[];
  preferences: { label: string; value: string }[];
  customFields: { label: string; value: string }[];
  tags: string[];
  /** Not in the artboard's rail, but a real feature of this screen — carried
   *  over rather than dropped in the repaint. */
  segments: { id: string; name: string }[];
  onOpenTicket: (id: string) => void;
  onAllTickets: () => void;
  onAddTag: () => void;
}

const MONO = 'font-mono';

/** Section rule: mono label, hairline, optional right action. */
function Rule({ label, action }: { label: string; action?: ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <span className={`${MONO} flex-none text-[9.5px] font-semibold tracking-[.2em] text-[var(--co-ink-4)]`}>
        {label}
      </span>
      <div className="h-px flex-1 bg-[var(--co-line-subtle)]" />
      {action}
    </div>
  );
}

function RailHeading({ children }: { children: ReactNode }) {
  return (
    <div className={`${MONO} mb-2 text-[9px] font-semibold tracking-[.2em] text-[var(--co-ink-4)]`}>
      {children}
    </div>
  );
}

/**
 * A group of reference rows in one bordered card, heading above it.
 *
 * The card is what separates reference material from the narrative in the left
 * column: a bare list of hairlines reads as more prose, a bounded card reads as
 * a table you consult.
 */
function RailCard({ children }: { children: ReactNode }) {
  return (
    <div className="mb-[22px] overflow-hidden rounded-[10px] border border-[var(--co-line)] bg-[var(--co-raised)]">{children}</div>
  );
}

/** A reference row: label left, value right. Never a heading. */
function RailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-[var(--co-line-subtle)] px-[11px] py-[9px] last:border-b-0">
      <span className="flex-none text-[12px] text-[var(--co-ink-3)]">{label}</span>
      <span
        // Identifiers, counts and dates are machine values and take the mono
        // face; words take the body face. Setting `CRM-88214` in the same face
        // as `Chat widget` makes both harder to scan.
        className={`min-w-0 truncate text-right text-[12px] text-[var(--co-ink)] ${
          mono ? `${MONO} tabular-nums` : ''
        }`}
      >
        {value}
      </span>
    </div>
  );
}

/** True for values that read as machine output rather than words. */
function looksMachine(value: string): boolean {
  return (
    /^[0-9]/.test(value) || // 14, 12 Jul 2027, 6 min ago
    /^[A-Z0-9][A-Z0-9._-]*[0-9]/.test(value) || // CRM-88214
    /^[a-z]{2}-[A-Z]{2}$/.test(value) // en-IN
  );
}

/** How long until (or since) a due time — '41m', '2h', 'overdue 3h'. */
function untilLabel(iso?: string): { text: string; breached: boolean } | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  const mins = Math.round((t - Date.now()) / 60000);
  const abs = Math.abs(mins);
  const span = abs < 60 ? `${abs}m` : abs < 1440 ? `${Math.round(abs / 60)}h` : `${Math.round(abs / 1440)}d`;
  return mins < 0 ? { text: `overdue ${span}`, breached: true } : { text: span, breached: false };
}

/** Age of a ticket, in the artboard's `2d 04h` shape. */
function ageLabel(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  if (d > 0) return `${d}d ${String(h).padStart(2, '0')}h`;
  const m = mins % 60;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

function relative(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return new Date(t).toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Urgent earns the burgundy; everything else is ink. Priority is a status. */
const PRIORITY_TONE: Record<string, string> = {
  urgent: 'var(--co-burgundy)',
  high: 'var(--co-terracotta-ink)',
  medium: 'var(--co-ink-3)',
  low: 'var(--co-ink-4)',
};

/** The marker's colour says WHO acted: the customer, an agent, or the system. */
const ACTOR_TONE: Record<string, string> = {
  customer: 'var(--co-machine)',
  agent: 'var(--co-ink)',
  system: 'var(--co-line-strong)',
};

export function ProfileOverview(p: ProfileOverviewProps) {
  const open = p.tickets.filter((t) => !['resolved', 'closed', 'merged'].includes(t.status));

  return (
    <div className="flex min-h-0 flex-1 items-stretch gap-[14px]">
      {/* ── Left: the finding, the work, the history ─────────────────────── */}
      <div className="scrollbar-hide min-w-0 flex-1 overflow-y-auto rounded-[14px] border border-[var(--co-line)] bg-[var(--co-raised)] shadow-[var(--co-shadow-card)] px-[24px] py-[22px]">
        {/* Rendered only when there is something real to say. A template with
            visible holes is worse than no panel — the same rule the Reports
            narrative band follows. */}
        {p.attention && (
          <div
            className="rounded-[12px] border px-[18px] py-[16px]"
            style={{ borderColor: 'var(--co-burgundy-line)', background: 'var(--co-burgundy-wash)' }}
          >
            <div className="flex items-start gap-3">
              <span
                className="flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full text-[11px] font-semibold"
                style={{ color: 'var(--co-burgundy)', boxShadow: 'inset 0 0 0 1px var(--co-burgundy-line)' }}
                aria-hidden="true"
              >
                !
              </span>
              <div className="min-w-0">
                <div className={`${MONO} text-[9.5px] font-semibold tracking-[.16em] text-[var(--co-burgundy)]`}>
                  {p.attention.headline}
                </div>
                <p
                  className="mt-2 max-w-[82ch] text-[13px] leading-[1.55] text-[var(--co-ink-2)]"
                  style={{ textWrap: 'pretty' } as React.CSSProperties}
                >
                  {p.attention.body}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className={p.attention ? 'mt-[22px]' : ''}>
          <Rule
            label="OPEN TICKETS"
            action={
              <button
                type="button"
                onClick={p.onAllTickets}
                className={`${MONO} flex-none text-[10px] font-medium tracking-[.1em] text-[var(--co-ink)] hover:underline`}
              >
                {`ALL ${p.totalTickets} →`}
              </button>
            }
          />
          <div className="mt-1">
            {open.length === 0 ? (
              <p className="py-4 text-[13px] text-[var(--co-ink-3)]">
                Nothing open. Every ticket this customer has raised is resolved or closed.
              </p>
            ) : (
              open.map((t) => {
                const due =
                  untilLabel(t.resolutionDueAt) ??
                  (t.firstResponseAt ? null : untilLabel(t.firstResponseDueAt));
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => p.onOpenTicket(t.id)}
                    className="my-1 flex w-full items-center gap-3 rounded-[10px] border border-l-[3px] border-[var(--co-line-subtle)] py-[11px] pl-3 pr-3 text-left transition-colors duration-[120ms] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)]"
                    style={{
                      borderLeftColor: due?.breached ? 'var(--co-burgundy)' : 'transparent',
                    }}
                  >
                    <span className={`${MONO} w-[54px] flex-none text-[11px] text-[var(--co-ink-3)]`}>
                      {`#${t.number}`}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-[var(--co-ink)]">
                      {t.subject}
                    </span>
                    <span
                      className={`${MONO} w-[86px] flex-none text-right text-[10px] font-semibold tracking-[.1em]`}
                      style={{ color: PRIORITY_TONE[t.priority] ?? 'var(--co-ink-3)' }}
                    >
                      {t.priority.toUpperCase()}
                    </span>
                    <span
                      className={`${MONO} w-[104px] flex-none text-right text-[11px] tabular-nums`}
                      style={{ color: due?.breached ? 'var(--co-burgundy)' : 'var(--co-ink-3)' }}
                    >
                      {due ? `SLA ${due.text}` : t.status.replace(/_/g, ' ')}
                    </span>
                    <span
                      className={`${MONO} w-[72px] flex-none text-right text-[11px] tabular-nums text-[var(--co-ink-3)]`}
                    >
                      {ageLabel(t.createdAt)}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="mt-[22px]">
          <Rule label="ACTIVITY" />
          <div className="mt-1">
            {p.activities.length === 0 ? (
              <p className="py-4 text-[13px] text-[var(--co-ink-3)]">
                No recorded activity yet.
              </p>
            ) : (
              p.activities.map((a) => (
                <div key={a.id} className="flex items-start gap-3 py-[9px]">
                  <span
                    className="mt-[5px] h-[8px] w-[8px] flex-none rounded-full"
                    style={{ background: ACTOR_TONE[a.performedByType ?? 'system'] }}
                    aria-hidden="true"
                  />
                  <div className="min-w-0 flex-1">
                    <div
                      className="text-[13px] leading-[1.45] text-[var(--co-ink)]"
                      style={{ textWrap: 'pretty' } as React.CSSProperties}
                    >
                      {a.description}
                    </div>
                    <div className={`${MONO} mt-[4px] text-[10px] tracking-[.08em] text-[var(--co-ink-4)]`}>
                      {[relative(a.createdAt), a.channel, a.performedBy]
                        .filter(Boolean)
                        .join(' · ')
                        .toUpperCase()}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* ── Right rail: facts about the record ───────────────────────────── */}
      <aside className="scrollbar-hide w-[280px] flex-none overflow-y-auto rounded-[14px] border border-[var(--co-line)] bg-[var(--co-raised)] shadow-[var(--co-shadow-card)] px-[18px] py-[22px]">
        {p.record.length > 0 && (
          <>
            <RailHeading>RECORD</RailHeading>
            <RailCard>
              {p.record.map((r) => (
                <RailRow key={r.label} {...r} mono={looksMachine(r.value)} />
              ))}
            </RailCard>
          </>
        )}

        {p.preferences.length > 0 && (
          <>
            <RailHeading>PREFERENCES</RailHeading>
            <RailCard>
              {p.preferences.map((r) => (
                <RailRow key={r.label} {...r} />
              ))}
            </RailCard>
          </>
        )}

        <RailHeading>TAGS</RailHeading>
        {/* Chips, not a card: they are values in their own right rather than
            label/value pairs, and boxing them twice reads as a nested table. */}
        <div className="mb-[22px] flex flex-wrap items-center gap-1.5">
          {p.tags.map((t) => (
            <span
              key={t}
              className={`${MONO} rounded-full border px-[9px] py-[3px] text-[9.5px] font-medium tracking-[.08em]`}
              style={{
                borderColor: 'var(--co-line-strong)',
                color: t.toUpperCase() === 'VIP' ? 'var(--co-ink)' : 'var(--co-ink-3)',
                fontWeight: t.toUpperCase() === 'VIP' ? 600 : 500,
              }}
            >
              {t.toUpperCase()}
            </span>
          ))}
          <button
            type="button"
            onClick={p.onAddTag}
            className={`${MONO} rounded-full border border-dashed border-[var(--co-line-strong)] px-[9px] py-[3px] text-[9.5px] tracking-[.08em] text-[var(--co-ink-3)] hover:border-[var(--co-ink)] hover:text-[var(--co-ink)]`}
          >
            + ADD
          </button>
        </div>

        {/* The artboard calls this PINNED NOTE. Nothing on the note record
            carries a pin, so this is the most recent one, labelled for what it
            actually is. */}
        {p.notes.length > 0 && (
          <>
            <RailHeading>LATEST NOTE</RailHeading>
            {/* Washed and left-marked rather than plain-bordered: it is somebody's
                sentence, not a field, and the one thing on this rail worth
                reading rather than consulting. */}
            <div
              className="mb-[22px] rounded-[10px] border border-l-[3px] px-3 py-2.5"
              style={{
                borderColor: 'var(--co-line)',
                borderLeftColor: 'var(--co-ink)',
                background: 'var(--co-brass-wash)',
              }}
            >
              <p
                className="text-[12px] leading-[1.5] text-[var(--co-ink-2)]"
                style={{ textWrap: 'pretty' } as React.CSSProperties}
              >
                {p.notes[0].content}
              </p>
              <div className={`${MONO} mt-2 text-[9.5px] tracking-[.08em] text-[var(--co-ink-4)]`}>
                {[p.notes[0].createdBy, relative(p.notes[0].createdAt)]
                  .filter(Boolean)
                  .join(' · ')
                  .toUpperCase()}
              </div>
            </div>
          </>
        )}

        {p.segments.length > 0 && (
          <>
            <RailHeading>SEGMENTS</RailHeading>
            <div className="mb-[22px] flex flex-wrap gap-1.5">
              {p.segments.map((s) => (
                <span
                  key={s.id}
                  className={`${MONO} rounded-full border border-[var(--co-line-strong)] px-[9px] py-[3px] text-[9.5px] font-medium tracking-[.08em] text-[var(--co-ink-3)]`}
                >
                  {s.name.toUpperCase()}
                </span>
              ))}
            </div>
          </>
        )}

        {p.customFields.length > 0 && (
          <>
            <RailHeading>CUSTOM FIELDS</RailHeading>
            <RailCard>
              {p.customFields.map((r) => (
                <RailRow key={r.label} {...r} mono={looksMachine(r.value)} />
              ))}
            </RailCard>
          </>
        )}
      </aside>
    </div>
  );
}

export default ProfileOverview;
